import React from 'react';
import { ResultItem } from '../resultItem/ResultItem';
import { Pagination } from '../pagination/Pagination';
import { SkeletonResultList } from '../skeletonResultList/SkeletonResultList';
import { HotelWidget } from '@/components/hotelWidget/HotelWidget';
import { Filters, ResultDataStats, ResultItem as ResultItemType } from '@/domain';
import { SortDetails } from 'shared/page/SortDetails';
import { useAdPositionElements, insertBanners } from '@schibsted-nmp/advertising-companion';
import { insertHotelWidget } from '@/lib/banner/resultListBanners';

export interface ResultListProps {
    stats: ResultDataStats;
    filters: Filters;
    onFilterChanged: (filter: Filters) => void;
    isMobile: boolean;
    numberOfPreloadedImages: number;
    results: ResultItemType[] | false | null;
    loginId: number;
}

export const ResultList = ({ stats, filters, onFilterChanged, isMobile, numberOfPreloadedImages, results, loginId }: ResultListProps) => {
    const [bannerPositions, bannerElements] = useAdPositionElements({
        cols: 2,
        type: 'search',
    });

    const renderItemNodes = () =>
        (results as ResultItemType[]).map((item, index) => (
            <ResultItem
                key={`${item.id}_${index}`}
                index={index}
                result={item}
                filters={filters}
                imagePreloaded={index < numberOfPreloadedImages}
                loginId={loginId}
                isMobile={isMobile}></ResultItem>
        ));

    const renderItemsAndPagination = () => {
        const items = renderItemNodes();

        const itemsWithHotelWidget = insertHotelWidget('hotel_widget_1', 8, filters, items);

        const adItemsWithBanners = insertBanners({
            adItems: itemsWithHotelWidget,
            bannerPositions,
            bannerElements,
        });

        return (
            <>
                <SortDetails section="feriehus" className="mb-16" />
                <section
                    aria-labelledby="ads-heading"
                    id="ads"
                    className="grid grid-flow-row-dense md:grid-cols-2 gap-16"
                    data-testid="resultList">
                    <h1 className="sr-only" id="ads-heading">
                        Søkeresultater
                    </h1>
                    {adItemsWithBanners}
                </section>
                <Pagination stats={stats} onFilterChanged={onFilterChanged} />
            </>
        );
    };

    const renderFetchFailedMessage = () => (
        <p className="p-16 text-22 font-bold text-center" data-testid="fetchFailed">
            Noe gikk galt under henting av tilbud. <a href="">Prøv å laste siden på nytt.</a>
        </p>
    );

    const renderNoResultsMessage = () => (
        <div>
            <p className="p-16 text-22 font-bold text-center" data-testid="emptyResultList">
                Ingen treff akkurat nå. Zoom ut i kartet for å få flere treff.
            </p>
            <HotelWidget filters={filters} />
        </div>
    );

    if (results === null) {
        return <SkeletonResultList isMobile={isMobile} />;
    }

    if (!results) {
        return renderFetchFailedMessage();
    }

    if (results.length === 0) {
        return renderNoResultsMessage();
    }

    return renderItemsAndPagination();
};
